import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";

import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { Badge } from "primereact/badge";
import { Dialog } from "primereact/dialog";

import UserSubscriptionsService from "../../services/UserSubscriptions/UserSubscriptions.service";
import { SubscriptionSummaryDTO, DetailedSubscriptionDTO, SubscriptionProduct } from "../../models/UserSubscriptions/UserSubscriptions-model";
import NewSubscriptionModal from "./NewUserSubscriptionsModal";
import { SubscriptionProductModal } from "./SubscriptionProductModal";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import UserAddressService from "../../services/useraddress/useraddress.service";

const MySubscriptionsPage = () => {
  const [subs, setSubs] = useState<SubscriptionSummaryDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [newModalVisible, setNewModalVisible] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionSummaryDTO | null>(null);
  const [detailedSubscription, setDetailedSubscription] = useState<DetailedSubscriptionDTO | null>(null);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [productModalVisible, setProductModalVisible] = useState<boolean>(false);
  const toast = useRef<Toast>(null);


  const loadData = async () => {
    setLoading(true);
    try {
      const data = await UserSubscriptionsService.getAllUserSubscriptions();
      setSubs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load subscriptions." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handlePauseResume = (sub: SubscriptionSummaryDTO) => {
    const isActive = !!sub.isActive;
    confirmDialog({
      message: isActive ? "Pause this subscription?" : "Resume this subscription?",
      header: isActive ? "Pause Subscription" : "Resume Subscription",
      acceptClassName: isActive ? "p-button-warning" : "p-button-success",
      accept: async () => {
        try {
          if (isActive) {
            await UserSubscriptionsService.pauseUserSubscription(sub.id);
            toast.current?.show({ severity: "success", summary: "Paused", detail: "Subscription paused." });
          } else {
            await UserSubscriptionsService.resumeUserSubscription(sub.id);
            toast.current?.show({ severity: "success", summary: "Resumed", detail: "Subscription resumed." });
          }
          loadData();
        } catch (e) {
          console.error(e);
          toast.current?.show({ severity: "error", summary: "Error", detail: "Action failed." });
        }
      }
    });
  };

  const handleCreateFlowContinue = async (subscriptionData: {
    deliveryAddressId: number;
    frequencyValue: number;
    dayOfMonth: number;
    products: { productId: number; quantity: number }[];
  }) => {
    try {
      setLoading(true);

      const payload = {
        deliveryAddressId: subscriptionData.deliveryAddressId,
        frequencyValue: subscriptionData.frequencyValue,
        dayOfMonth: subscriptionData.dayOfMonth,
        products: subscriptionData.products,
        startDate: new Date().toISOString(), 
        frequencyType: "days", 
        isActive: true
      };

      const result = await UserSubscriptionsService.createUserSubscription(payload);

      if (result) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Subscription created successfully!"
        });

        await loadData();

        setNewModalVisible(false);
      } else {
        throw new Error("Failed to create subscription");
      }
    } catch (error: any) {
      console.error("Error creating subscription:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to create subscription. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubscription = async (subscription: SubscriptionSummaryDTO) => {
    try {
      setLoadingDetails(true);
      setSelectedSubscription(subscription);

      const addressData = await UserAddressService.getUserAddressById(subscription.userId);

      let formattedAddresses = [];
      if (Array.isArray(addressData)) {
        formattedAddresses = addressData
          .filter((addr: any) => !addr.isDeleted)
          .map((addr: any) => ({
            id: addr.userAddressID || addr.id,
            label: `${addr.address1}${addr.address2 ? ', ' + addr.address2 : ''}, ${addr.areaName || addr.area || 'Unknown Area'}`,
            value: addr.userAddressID || addr.id,
            ...addr
          }));
      } else {
        console.warn("Address data is not an array:", addressData);
        formattedAddresses = [{
          id: subscription.deliveryAddressId,
          label: `Address ID: ${subscription.deliveryAddressId} (Mock Address)`,
          value: subscription.deliveryAddressId
        }];
      }

      setAddresses(formattedAddresses);

      const mockDetailedSubscription: DetailedSubscriptionDTO = {
        id: subscription.id,
        userId: subscription.userId,
        deliveryAddressId: subscription.deliveryAddressId,
        frequencyValue: subscription.frequencyValue,
        frequencyType: "days",
        dayOfMonth: 15,
        startDate: new Date().toISOString(),
        isActive: subscription.isActive || true,
        status: subscription.status || "Active",
        createdDate: new Date().toISOString(),
        products: [
          {
            productId: 1,
            productName: "Sample Product 1",
            quantity: 2,
            unitPrice: 100,
            imageUrl: "",
            categoryName: "Category 1",
            subCategoryName: "Sub Category 1"
          },
          {
            productId: 2,
            productName: "Sample Product 2",
            quantity: 1,
            unitPrice: 150,
            imageUrl: "",
            categoryName: "Category 2",
            subCategoryName: "Sub Category 2"
          }
        ],
        deliveryAddress: {
          id: subscription.deliveryAddressId,
          address1: "Sample Address Line 1",
          address2: "Sample Address Line 2",
          landmark: "Near Sample Landmark",
          areaName: "Sample Area",
          subDistrictName: "Sample Sub District",
          cityName: "Sample City",
          stateName: "Sample State",
          zipcode: "123456"
        }
      };

      setDetailedSubscription(mockDetailedSubscription);

      setEditFormData({
        frequencyValue: mockDetailedSubscription.frequencyValue,
        deliveryAddressId: mockDetailedSubscription.deliveryAddressId,
        dayOfMonth: mockDetailedSubscription.dayOfMonth,
        isActive: mockDetailedSubscription.isActive,
        products: [...mockDetailedSubscription.products]
      });

      setEditModalVisible(true);
    } catch (error) {
      console.error("Error loading subscription details:", error);

      setAddresses([]);
      setDetailedSubscription(null);
      setEditFormData(null);

      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load subscription details"
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleUpdateSubscription = async () => {
    try {
      setLoading(true);

      if (!editFormData || !selectedSubscription) {
        throw new Error("No data to update");
      }

      const updatePayload = {
        id: selectedSubscription.id,
        deliveryAddressId: editFormData.deliveryAddressId,
        frequencyValue: editFormData.frequencyValue,
        dayOfMonth: editFormData.dayOfMonth,
        isActive: editFormData.isActive,
        products: editFormData.products
      };

      const result = await UserSubscriptionsService.updateUserSubscription(updatePayload);

      if (result) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Subscription updated successfully!"
        });

        await loadData();

        handleCloseEditModal();
      } else {
        throw new Error("Failed to update subscription");
      }
    } catch (error: any) {
      console.error("Error updating subscription:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to update subscription. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalVisible(false);
    setSelectedSubscription(null);
    setDetailedSubscription(null);
    setEditFormData(null);
    setProductModalVisible(false);
    setAddresses([]);
  };

  const handleProductUpdate = (updatedProducts: SubscriptionProduct[]) => {
    setEditFormData({
      ...editFormData,
      products: updatedProducts
    });
    setProductModalVisible(false);
  };

  const handleFormFieldChange = (field: string, value: any) => {
    setEditFormData({
      ...editFormData,
      [field]: value
    });
  };

  const SubscriptionCard = ({ subscription }: { subscription: SubscriptionSummaryDTO }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Card
        className="mb-3 h-full flex flex-column"
        style={{
          boxShadow: isHovered
            ? "0 8px 20px rgba(144, 238, 144, 0.7)"
            : "0 4px 12px rgba(144, 238, 144, 0.5)",
          borderRadius: "8px",
          border: "1px solid #e2f5e2",
          transform: isHovered ? "scale(1.03)" : "scale(1)",
          transition: "all 0.3s ease",
          zIndex: isHovered ? 10 : 1,
          height: "100%"
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-column flex-grow-1 p-2">
          <Badge
            value={subscription.isActive ? "Active" : "Inactive"}
            severity={subscription.isActive ? "success" : "danger"}
            className="absolute top-0 right-0 m-1"
            style={{ fontSize: '0.8rem' }}
          />

          <div className="text-xs text-500 mb-1">SUBSCRIPTION</div>
          <div className="font-bold mb-2 text-lg">#{subscription.id}</div>

          <div className="flex flex-column gap-2 mb-3">
            <div className="flex align-items-center justify-content-between">
              <div className="text-600 text-sm">Frequency</div>
              <div className="font-semibold">{subscription.frequencyValue} days</div>
            </div>
            <div className="flex align-items-center justify-content-between">
              <div className="text-600 text-sm">Address ID</div>
              <div className="font-semibold">{subscription.deliveryAddressId}</div>
            </div>
          </div>

          <div className="flex justify-content-around mt-auto gap-1">
            <Button
              icon={subscription.isActive ? "pi pi-pause" : "pi pi-play"}
              label={subscription.isActive ? "Pause" : "Resume"}
              onClick={() => handlePauseResume(subscription)}
              className={subscription.isActive ? "p-button-warning text-xs" : "p-button-success text-xs"}
              style={{ minWidth: "80px", padding: "0.3rem" }}
            />
            <Button
              icon="pi pi-pencil"
              label="Edit"
              onClick={() => handleEditSubscription(subscription)}
              className="p-button-outlined text-xs"
              style={{ minWidth: "60px", padding: "0.3rem" }}
            />
          </div>
        </div>
      </Card>
    );
  };

  const renderCards = () => {
    if (loading) {
      return (
        <div className="flex justify-content-center align-items-center" style={{ minHeight: 240 }}>
          <ProgressSpinner />
        </div>
      );
    }

    if (!subs.length) {
      return (
        <div className="surface-100 border-round p-4 text-center">
          <div className="text-900 text-xl mb-2">No active subscriptions yet</div>
          <div className="text-600 mb-3">Create your first one by clicking the button above.</div>
        </div>
      );
    }

    return (
      <div className="grid mt-3" style={{ alignItems: "stretch" }}>
        {subs.map((subscription) => (
          <div
            key={subscription.id}
            className="col-12 md:col-6 lg:col-4 p-1"
          >
            <SubscriptionCard subscription={subscription} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-3">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="flex justify-content-between align-items-center mb-2">
        <h2 className="m-0">My Subscription</h2>
        <Button label="New Subscription" icon="pi pi-plus" className="p-button-success" onClick={() => setNewModalVisible(true)} />
      </div>

      {renderCards()}

      <NewSubscriptionModal
        visible={newModalVisible}
        onHide={() => setNewModalVisible(false)}
        onAddProducts={handleCreateFlowContinue}
      />

      <Dialog
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-pencil text-primary"></i>
            <span>Edit Subscription #{selectedSubscription?.id}</span>
          </div>
        }
        visible={editModalVisible}
        onHide={handleCloseEditModal}
        style={{ width: '800px', maxHeight: '90vh' }}
        modal
        className="p-fluid"
      >
        {loadingDetails ? (
          <div className="flex justify-content-center align-items-center p-5">
            <ProgressSpinner />
            <span className="ml-2">Loading subscription details...</span>
          </div>
        ) : (
          detailedSubscription && editFormData && (
            <div className="flex flex-column gap-4">

              <div className="surface-100 p-3 border-round">
                <div className="flex align-items-center justify-content-between mb-3">
                  <h4 className="m-0">Subscription Status</h4>
                  <Badge
                    value={editFormData.isActive ? "Active" : "Inactive"}
                    severity={editFormData.isActive ? "success" : "danger"}
                  />
                </div>
                <div className="flex align-items-center gap-2">
                  <Checkbox
                    inputId="isActive"
                    checked={editFormData.isActive}
                    onChange={(e) => handleFormFieldChange('isActive', e.checked)}
                  />
                  <label htmlFor="isActive" className="font-semibold">
                    {editFormData.isActive ? 'Subscription is Active' : 'Subscription is Inactive'}
                  </label>
                </div>
              </div>

              <div className="surface-100 p-3 border-round">
                <h4 className="mt-0 mb-3">Delivery Details</h4>

                <div className="grid">
                  <div className="col-12 md:col-6">
                    <label htmlFor="frequency" className="font-semibold mb-2 block">
                      Delivery Frequency (days)
                    </label>
                    <InputNumber
                      id="frequency"
                      value={editFormData.frequencyValue}
                      onValueChange={(e) => handleFormFieldChange('frequencyValue', e.value)}
                      min={1}
                      max={365}
                      placeholder="Enter frequency in days"
                    />
                  </div>

                  <div className="col-12 md:col-6">
                    <label htmlFor="dayOfMonth" className="font-semibold mb-2 block">
                      Day of Month
                    </label>
                    <InputNumber
                      id="dayOfMonth"
                      value={editFormData.dayOfMonth}
                      onValueChange={(e) => handleFormFieldChange('dayOfMonth', e.value)}
                      min={1}
                      max={31}
                      placeholder="Day of month"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <label htmlFor="address" className="font-semibold mb-2 block">
                    Delivery Address
                  </label>
                  <Dropdown
                    id="address"
                    value={editFormData.deliveryAddressId}
                    options={Array.isArray(addresses) ? addresses : []}
                    onChange={(e) => handleFormFieldChange('deliveryAddressId', e.value)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select delivery address"
                    className="w-full"
                    filter
                    showClear
                    disabled={!Array.isArray(addresses) || addresses.length === 0}
                  />
                </div>

                <div className="mt-3 p-2 surface-200 border-round">
                  <div className="text-sm text-600 mb-1">Current Address:</div>
                  <div className="text-sm">
                    {detailedSubscription.deliveryAddress.address1}
                    {detailedSubscription.deliveryAddress.address2 && `, ${detailedSubscription.deliveryAddress.address2}`}
                    <br />
                    {detailedSubscription.deliveryAddress.landmark && `${detailedSubscription.deliveryAddress.landmark}, `}
                    {detailedSubscription.deliveryAddress.areaName}, {detailedSubscription.deliveryAddress.subDistrictName}
                    <br />
                    {detailedSubscription.deliveryAddress.cityName}, {detailedSubscription.deliveryAddress.stateName} - {detailedSubscription.deliveryAddress.zipcode}
                  </div>
                </div>
              </div>

              <Divider />

              <div className="surface-100 p-3 border-round">
                <div className="flex align-items-center justify-content-between mb-3">
                  <h4 className="m-0">Subscription Products ({editFormData.products?.length || 0})</h4>
                  <Button
                    label="Manage Products"
                    icon="pi pi-shopping-cart"
                    className="p-button-outlined"
                    onClick={() => setProductModalVisible(true)}
                  />
                </div>

                {editFormData.products && editFormData.products.length > 0 ? (
                  <div className="grid">
                    {editFormData.products.map((product: SubscriptionProduct) => (
                      <div key={product.productId} className="col-12 md:col-6 lg:col-4">
                        <Card className="h-full">
                          <div className="flex flex-column gap-2">
                            <div className="font-semibold text-sm">{product.productName}</div>
                            <div className="text-600 text-xs">
                              {product.categoryName} - {product.subCategoryName}
                            </div>
                            <div className="flex justify-content-between align-items-center">
                              <span className="text-sm">Quantity: {product.quantity}</span>
                              {product.unitPrice && (
                                <span className="text-sm font-semibold">₹{product.unitPrice}</span>
                              )}
                            </div>
                          </div>
                        </Card>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-4 text-600">
                    No products selected. Click "Manage Products" to add products.
                  </div>
                )}
              </div>

              <div className="flex justify-content-end gap-2 mt-4">
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  className="p-button-outlined"
                  onClick={handleCloseEditModal}
                />
                <Button
                  label="Save Changes"
                  icon="pi pi-check"
                  className="p-button-success"
                  onClick={handleUpdateSubscription}
                  loading={loading}
                  disabled={!editFormData.products || editFormData.products.length === 0}
                />
              </div>
            </div>
          )
        )}
      </Dialog>

      <SubscriptionProductModal
        visible={productModalVisible}
        onHide={() => setProductModalVisible(false)}
        onConfirm={handleProductUpdate}
        initialProducts={editFormData?.products || []}
      />
    </div>
  );
};

export default MySubscriptionsPage;
